import "./Pagination.css"

import { useCallback, useLayoutEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { GrFormNext, GrFormPrevious } from "react-icons/gr"
import { useNavigate, useParams } from "react-router"

import PaginationButton from "./PaginationButton/PaginationButton"

interface PaginationProps {
  pagination: {
    last_page: number
  }
  route?: string
}

export default function Pagination({ pagination, route }: PaginationProps) {
  const { page } = useParams()
  const navigate = useNavigate()
  const [inputError, setInputError] = useState("")
  const [inputValue, setInputValue] = useState(page || "1")

  const currentPage = Number(page) || 1

  useLayoutEffect(() => {
    if (currentPage > +pagination?.last_page || currentPage < 1) {
      navigate(`/${route}/1`)
    }
  }, [navigate, currentPage, pagination, route])

  const updatePage = useCallback(
    (pageNum: number) => {
      navigate(`/${route}/${pageNum}`)
      setInputValue(pageNum.toString()) // Update input value when navigating
    },
    [navigate, route]
  )

  const pageElements = useMemo(() => {
    const elements: React.ReactNode[] = []
    const total = 9
    const gap = Math.floor((total - 1) / 2)
    let start = Math.max(1, currentPage - gap)
    let end = Math.min(pagination?.last_page, currentPage + gap)

    if (end - start + 1 < total) {
      if (+currentPage - gap < 1) {
        end = Math.min(pagination?.last_page, end + (1 - (+currentPage - gap)))
      }
      if (+currentPage + gap > pagination?.last_page) {
        start = Math.max(
          1,
          start - (+currentPage + gap - pagination?.last_page)
        )
      }
    }

    for (let i = start; i <= end; i++) {
      elements.push(
        <PaginationButton
          className={currentPage === i ? "active" : ""}
          key={`${route}-pages-${i}`}
          onClick={() => updatePage(i)}
        >
          {i}
        </PaginationButton>
      )
    }

    if (end < pagination?.last_page) {
      elements.push(
        <div
          className="d-flex gap-2 align-items-center"
          key={`more-pages-${route}`}
        >
          {end < pagination.last_page - 1 && <div>...</div>}
          <PaginationButton
            className={currentPage === pagination?.last_page ? "active" : ""}
            onClick={() => updatePage(pagination?.last_page)}
          >
            {pagination?.last_page}
          </PaginationButton>
        </div>
      )
    }

    return elements
  }, [pagination?.last_page, currentPage, route, updatePage])

  const { t } = useTranslation()

  if (pagination?.last_page === 1 || !pagination) return null

  return (
    <>
      <div className="d-flex align-items-center justify-content-center gap-5 mt-3">
        <div className="d-flex justify-content-center gap-2 align-items-center">
          {currentPage > 1 && (
            <div
              key={`prev-page-${route}`}
              className="text--primary cursor-pointer"
              onClick={() => updatePage(currentPage - 1)}
            >
              <GrFormPrevious /> {t("buttons.prev")}
            </div>
          )}
          {pageElements}
          {currentPage < pagination?.last_page && (
            <div
              key={`next-page-${route}`}
              className="text--primary cursor-pointer"
              onClick={() => updatePage(currentPage + 1)}
            >
              {t("buttons.next")} <GrFormNext />
            </div>
          )}
        </div>

        <div
          className={`d-flex align-items-center gap-3 position-relative ${
            inputError && "pagination__error"
          }`}
        >
          <span>{t("buttons.goto")}</span>
          <input
            type="number"
            min="1"
            max={pagination?.last_page}
            value={inputValue}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !inputError) {
                const pageNumber = parseInt(inputValue, 10)
                if (pageNumber >= 1 && pageNumber <= pagination?.last_page) {
                  updatePage(pageNumber)
                }
              }
            }}
            onChange={(e) => {
              const value = e.target.value
              setInputValue(value)

              if (
                value === "" ||
                (Number(value) >= 1 && Number(value) <= pagination?.last_page)
              ) {
                setInputError("")
              } else {
                setInputError(t("pagination.errors.invalidPage"))
              }
            }}
            className={`pagination__input ${
              inputError && "pagination__input__error"
            }`}
          />
          <span>{t("pagination.data.page")}</span>
          {inputError && (
            <span className="pagination__input__error text-danger">
              {inputError}
            </span>
          )}
        </div>
      </div>
    </>
  )
}
